import { useCallback, useEffect, useRef, useState } from "react";

export type PreviewStatus =
  | "idle"
  | "generating"
  | "ready"
  | "failed"
  | "stale";

export interface PreviewState {
  status: PreviewStatus;
  previewUrl: string | null;
  previewHash: string | null;
  generatedAt: Date | null;
  error: string | null;
}

interface PreviewTriggerPayload {
  agreementId?: string;
  agreementData: Record<string, unknown>;
  isDefaultsPreview?: boolean;
  /**
   * Sample agreement body to render with. Required when previewing
   * defaults so the page is visually populated; the renderer ignores
   * it when an `agreementId` is supplied because the saved body is
   * loaded from the row.
   */
  sampleAgreementHtml?: string;
}

interface UsePreviewIntegrationOptions {
  /** Initial hash from the load query, if a preview already exists. */
  initialHash: string | null;
  /** Initial CDN URL or presigned URL of the existing preview, if any. */
  initialPreviewUrl?: string | null;
  /** Initial generated_at timestamp, if any. */
  initialGeneratedAt?: string | null;
  /** Idle ms before auto-refresh fires. */
  idleMs?: number;
  /** Build the trigger body. Called every time we POST. */
  buildTriggerPayload: () => PreviewTriggerPayload;
}

interface PreviewIntegration extends PreviewState {
  /** Fire a preview right now (manual refresh button). */
  triggerPreview: () => void;
  /** Mark the preview as stale because the editor changed. Restarts the idle timer. */
  markStale: () => void;
  /** True when content has been edited since the last successful render. */
  isStale: boolean;
}

const POLL_MS = 2000;
// Generous: most renders land in 5-30s but we've seen 1:20+, so allow
// 3 minutes before we give up on the DAG run.
const POLL_TIMEOUT_MS = 180_000;
// Reference value used by the UI to draw the progress bar / countdown.
// Not a hard cap.
export const PREVIEW_ETA_MS = 60_000;

interface TriggerResponse {
  previewId: string;
  status: string;
}

interface StatusResponse {
  status: "queued" | "running" | "ready" | "failed";
  url?: string;
  detail?: string;
}

export function usePreviewIntegration(
  options: UsePreviewIntegrationOptions,
): PreviewIntegration {
  const {
    initialHash,
    initialPreviewUrl,
    initialGeneratedAt,
    idleMs = 20000,
    buildTriggerPayload,
  } = options;

  const [state, setState] = useState<PreviewState>(() => ({
    status: initialHash ? "ready" : "idle",
    previewUrl: initialPreviewUrl ?? null,
    previewHash: initialHash ?? null,
    generatedAt: initialGeneratedAt ? new Date(initialGeneratedAt) : null,
    error: null,
  }));
  const [isStale, setIsStale] = useState(false);

  const idleTimerRef = useRef<number | null>(null);
  const pollTimerRef = useRef<number | null>(null);
  const pollDeadlineRef = useRef<number>(0);
  const activePreviewIdRef = useRef<string | null>(null);
  const buildPayloadRef = useRef(buildTriggerPayload);
  buildPayloadRef.current = buildTriggerPayload;

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current != null) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current != null) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const triggerPreview = useCallback(async () => {
    clearIdleTimer();
    clearPollTimer();

    setState((prev) => ({ ...prev, status: "generating", error: null }));

    let previewId: string;
    try {
      const payload = buildPayloadRef.current();
      const res = await fetch("/api/agreements/preview", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const detail = await res.text();
        throw new Error(detail || `HTTP ${res.status}`);
      }
      const json = (await res.json()) as TriggerResponse;
      previewId = json.previewId;
      activePreviewIdRef.current = previewId;
    } catch (err) {
      setState((prev) => ({
        ...prev,
        status: "failed",
        error: err instanceof Error ? err.message : "Preview trigger failed",
      }));
      return;
    }

    pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;

    const poll = async () => {
      if (activePreviewIdRef.current !== previewId) return;
      if (Date.now() > pollDeadlineRef.current) {
        setState((prev) => ({
          ...prev,
          status: "failed",
          error: "Preview timed out. Try again.",
        }));
        return;
      }

      try {
        const res = await fetch(
          `/api/agreements/preview/${encodeURIComponent(previewId)}/status`,
          { credentials: "include" },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as StatusResponse;
        if (json.status === "ready") {
          // The hash and the persisted preview metadata are written by
          // the Airflow renderer (WS3). The caller refetches the
          // tenantSettings / agreement query after this completes to
          // pick up the new hash & image URL.
          setState({
            status: "ready",
            previewUrl: json.url ?? null,
            previewHash: null, // caller refetches and supplies the hash
            generatedAt: new Date(),
            error: null,
          });
          setIsStale(false);
        } else if (json.status === "failed") {
          setState((prev) => ({
            ...prev,
            status: "failed",
            error: json.detail ?? "Preview render failed",
          }));
        } else {
          pollTimerRef.current = window.setTimeout(poll, POLL_MS);
        }
      } catch (err) {
        setState((prev) => ({
          ...prev,
          status: "failed",
          error: err instanceof Error ? err.message : "Preview poll failed",
        }));
      }
    };

    pollTimerRef.current = window.setTimeout(poll, POLL_MS);
  }, [clearIdleTimer, clearPollTimer]);

  const markStale = useCallback(() => {
    setIsStale(true);
    setState((prev) =>
      prev.status === "generating" ? prev : { ...prev, status: "stale" },
    );
    clearIdleTimer();
    idleTimerRef.current = window.setTimeout(() => {
      void triggerPreview();
    }, idleMs);
  }, [clearIdleTimer, idleMs, triggerPreview]);

  useEffect(() => {
    return () => {
      clearIdleTimer();
      clearPollTimer();
      activePreviewIdRef.current = null;
    };
  }, [clearIdleTimer, clearPollTimer]);

  return {
    ...state,
    isStale,
    triggerPreview: () => {
      void triggerPreview();
    },
    markStale,
  };
}
